import React from 'react';

/**
 * The art behind a slide. One Python logo on every slide of a lesson about
 * operators says nothing about the slide it is behind; these motifs do.
 *
 * None of it moves. A projector has one subject at a time and it is never
 * the wallpaper — motion here would pull the room's eye off the thing being
 * taught. What does move in this deck is content arriving: the bill's three
 * answers, the operator cards. Decoration holds still.
 */

// Official Python logo paths (128x128 viewBox with translate(0, 10.26))
const PY_TOP =
  'M63.391 1.988c-4.222.02-8.252.379-11.8 1.007-10.45 1.846-12.346 5.71-12.346 12.837v9.411h24.693v3.137H29.977c-7.176 0-13.46 4.313-15.426 12.521-2.268 9.405-2.368 15.275 0 25.096 1.755 7.311 5.947 12.519 13.124 12.519h8.491V67.234c0-8.151 7.051-15.34 15.426-15.34h24.665c6.866 0 12.346-5.654 12.346-12.548V15.833c0-6.693-5.646-11.72-12.346-12.837-4.244-.706-8.645-1.027-12.866-1.008zM50.037 9.557c2.55 0 4.634 2.117 4.634 4.721 0 2.593-2.083 4.69-4.634 4.69-2.56 0-4.633-2.097-4.633-4.69-.001-2.604 2.073-4.721 4.633-4.721z';
const PY_BOTTOM =
  'M91.682 28.38v10.966c0 8.5-7.208 15.655-15.426 15.655H51.591c-6.756 0-12.346 5.783-12.346 12.549v23.515c0 6.691 5.818 10.628 12.346 12.547 7.816 2.297 15.312 2.713 24.665 0 6.216-1.801 12.346-5.423 12.346-12.547v-9.412H63.938v-3.138h37.012c7.176 0 9.852-5.005 12.348-12.519 2.578-7.735 2.467-15.174 0-25.096-1.774-7.145-5.161-12.521-12.348-12.521h-9.268zM77.809 87.927c2.561 0 4.634 2.097 4.634 4.692 0 2.602-2.074 4.719-4.634 4.719-2.55 0-4.633-2.117-4.633-4.719 0-2.595 2.083-4.692 4.633-4.692z';

const NAVY = 'var(--color-navy)';
const GOLD = 'var(--color-gold-deep)';

/* The signature: the language itself. Title and closing slides only. */
function Logo() {
  return (
    <g className="bgm bgm-logo" transform="translate(1300, 470) scale(4.8)">
      <g transform="scale(1.1) translate(-6, -6)">
        <path d={PY_TOP} transform="translate(0 10.26)" fill="none" stroke={NAVY} strokeWidth="1" strokeDasharray="4 6" />
        <path d={PY_BOTTOM} transform="translate(0 10.26)" fill="none" stroke={GOLD} strokeWidth="1" strokeDasharray="4 6" />
      </g>
      <path d={PY_TOP} transform="translate(0 10.26)" fill="none" stroke={NAVY} strokeWidth="1.8" strokeLinejoin="round" />
      <path d={PY_BOTTOM} transform="translate(0 10.26)" fill="none" stroke={GOLD} strokeWidth="1.8" strokeLinejoin="round" />
    </g>
  );
}

/* Slow concentric rings — for slides that set out where the lesson is going. */
function Orbit() {
  return (
    <g className="bgm bgm-orbit" transform="translate(1520, 560)">
      {[120, 200, 290, 390].map((r, i) => (
        <circle key={r} r={r} fill="none" stroke={i % 2 ? GOLD : NAVY} strokeWidth="1.4"
          strokeDasharray={i % 2 ? '3 9' : undefined} />
      ))}
      <circle cx="290" cy="0" r="7" fill={GOLD} />
      <circle cx="-200" cy="0" r="5" fill={NAVY} />
    </g>
  );
}

/* One amount breaking into equal shares, with something left over. */
function Coins() {
  return (
    <g className="bgm bgm-coins" transform="translate(1440, 540)">
      <circle className="bgm-coin-whole" r="160" fill="none" stroke={NAVY} strokeWidth="2" />
      {[0, 1, 2].map((i) => (
        <circle key={i} className="bgm-coin-share" r="74" fill="none" stroke={GOLD} strokeWidth="2"
          style={{ transform: `rotate(${i * 120}deg) translateY(-230px)` }} />
      ))}
      <circle className="bgm-coin-left" cy="330" r="22" fill="none" stroke={GOLD} strokeWidth="2.5" strokeDasharray="4 5" />
    </g>
  );
}

/* Rings going out, the way an open question goes out to the room. */
function Pulse() {
  return (
    <g className="bgm bgm-pulse" transform="translate(1560, 620)">
      {[0, 1, 2, 3].map((i) => (
        <circle key={i} r={90 + i * 78} fill="none" stroke={i % 2 ? GOLD : NAVY} strokeWidth="2" />
      ))}
    </g>
  );
}

/* A drifting field of the seven operators, for the slide that names them. */
const OPS = ['+', '-', '*', '/', '//', '%', '**'];
function OpField() {
  return (
    <g className="bgm bgm-ops">
      {OPS.map((op, i) => {
        const x = 120 + ((i * 271) % 1700);
        const y = 140 + ((i * 397) % 820);
        return (
          <text key={op + i} className="bgm-op" x={x} y={y} fill={i % 3 === 0 ? GOLD : NAVY}
            style={{ fontSize: `${54 + (i % 3) * 22}px` }}>
            {op}
          </text>
        );
      })}
      {OPS.map((op, i) => {
        const x = 300 + ((i * 433) % 1500);
        const y = 320 + ((i * 251) % 640);
        return (
          <text key={`b${op}${i}`} className="bgm-op" x={x} y={y} fill={i % 2 ? NAVY : GOLD}
            style={{ fontSize: `${36 + (i % 2) * 16}px` }}>
            {op}
          </text>
        );
      })}
    </g>
  );
}

/* A value travelling down a path — the slide where one expression reduces. */
function Flow() {
  const d = 'M 1180 180 H 1560 A 40 40 0 0 1 1600 220 V 420 A 40 40 0 0 1 1560 460 H 1280 A 40 40 0 0 0 1240 500 V 720 A 40 40 0 0 0 1280 760 H 1660';
  return (
    <g className="bgm bgm-flow">
      <path d={d} fill="none" stroke={NAVY} strokeWidth="2" strokeDasharray="10 8" />
      <circle cx="1180" cy="180" r="9" fill={GOLD} />
      <circle cx="1660" cy="760" r="9" fill={NAVY} />
    </g>
  );
}

/* Descending blocks — order of operations, and the part dividers. */
function Stairs() {
  return (
    <g className="bgm bgm-stairs" transform="translate(1180, 260)">
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={i * 130} y={i * 120} width="250" height="70" rx="14"
          fill="none" stroke={i === 0 ? GOLD : NAVY} strokeWidth="2" />
      ))}
    </g>
  );
}

/* Bars coming up, for the slide that ranks the class. */
function Bars() {
  return (
    <g className="bgm bgm-bars" transform="translate(1320, 880)">
      {[220, 340, 170, 430, 260].map((h, i) => (
        <rect key={i} x={i * 96} y={-h} width="58" height={h} rx="12"
          fill="none" stroke={i === 3 ? GOLD : NAVY} strokeWidth="2" />
      ))}
    </g>
  );
}

/* Brackets and a cursor, for the slides that show real code. */
function Code() {
  return (
    <g className="bgm bgm-code" transform="translate(1360, 300)">
      <path d="M 120 0 C 40 0 40 60 40 130 C 40 200 0 200 0 200 C 0 200 40 200 40 270 C 40 340 40 400 120 400"
        fill="none" stroke={NAVY} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M 380 0 C 460 0 460 60 460 130 C 460 200 500 200 500 200 C 500 200 460 200 460 270 C 460 340 460 400 380 400"
        fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" />
      <rect x="230" y="170" width="16" height="60" rx="3" fill={NAVY} />
    </g>
  );
}

// Which motif belongs behind which kind of slide.
const MOTIF = {
  cover: Logo,
  outro: Logo,
  objectives: Orbit,
  glossary: Orbit,
  'bill-split': Coins,
  hook: Coins,
  quiz: Pulse,
  'op-table': OpField,
  'select-type': OpField,
  'calc-trace': Flow,
  pipeline: Flow,
  overview: Flow,
  precedence: Stairs,
  divider: Stairs,
  results: Bars,
  step: Code,
};

/* Theme A's backdrop: a strip of the lesson part's colour along the top
   edge, and two oversized paper tags, faint, in the corners — the lesson's
   own object, not a generic motif. Colours come from the theme's
   --sec / --sec-soft variables, set per lesson part in themeTag.css. */
function TagBackdrop() {
  const tag = 'M70 0 H420 V230 H70 L0 115 Z';
  return (
    <div className="tag-backdrop" aria-hidden="true">
      <div className="tag-backdrop-band" />
      <svg className="tag-backdrop-svg" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        <g transform="translate(1530 690) rotate(-18) scale(1.15)">
          <path d={tag} className="tag-backdrop-tag" />
          <circle cx="62" cy="115" r="18" className="tag-backdrop-hole" />
        </g>
        <g transform="translate(-120 -40) rotate(14) scale(0.8)">
          <path d={tag} className="tag-backdrop-tag" />
          <circle cx="62" cy="115" r="18" className="tag-backdrop-hole" />
        </g>
      </svg>
    </div>
  );
}

export default function DeckBackground({ kind, theme }) {
  if (theme === 'tag') return <TagBackdrop />;
  const Motif = MOTIF[kind] || Orbit;
  return (
    <div className="deck-bg-art" aria-hidden="true">
      <div className="deck-bg-glow deck-bg-glow-gold" />
      <div className="deck-bg-glow deck-bg-glow-blue" />
      <svg className="deck-bg-svg" viewBox="0 0 1920 1080" fill="none"
        xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
        {/* Blueprint crossmarks, the one thing every slide keeps. */}
        <g className="deck-bg-crossmarks" stroke="currentColor">
          <path d="M 240 160 v 14 M 233 167 h 14" />
          <path d="M 960 120 v 14 M 953 127 h 14" />
          <path d="M 1680 200 v 14 M 1673 207 h 14" />
          <path d="M 180 840 v 14 M 173 847 h 14" />
          <path d="M 880 940 v 14 M 873 947 h 14" />
        </g>
        <Motif />
      </svg>
    </div>
  );
}
